<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes, faCalendar, faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons';
  import { user, userRole } from '$stores/sessionStore';

  // Props
  export let show = false;
  export let title = 'Select Date';
  export let message = 'Please select a date';
  export let confirmButtonText = 'Confirm';
  export let cancelButtonText = 'Cancel';
  export let selectedDate = new Date();

  const dispatch = createEventDispatcher();
  
  // Calendar state
  let currentMonth = selectedDate.getMonth();
  let currentYear = selectedDate.getFullYear();
  let today = new Date();
  today = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Format date as MM/DD/YYYY for display
  function formatDateForDisplay(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  function handleConfirm() {
    dispatch('confirm', { selectedDate });
    show = false;
  }

  function handleCancel() {
    dispatch('cancel');
    show = false;
  }

  function previousMonth() {
    if (currentMonth === 0) {
      currentMonth = 11;
      currentYear--;
    } else {
      currentMonth--;
    }
    // Force a re-render of calendar days
    calendarDays = generateCalendarDays();
  }

  function nextMonth() {
    if (currentMonth === 11) {
      currentMonth = 0;
      currentYear++;
    } else {
      currentMonth++;
    }
    // Force a re-render of calendar days
    calendarDays = generateCalendarDays();
  }

  function selectDate(year, month, day) {
    selectedDate = new Date(year, month, day);
  }

  function isToday(year, month, day) {
    const date = new Date(year, month, day);
    return date.getTime() === today.getTime();
  }

  function isSelected(year, month, day) {
    return selectedDate.getFullYear() === year && 
           selectedDate.getMonth() === month && 
           selectedDate.getDate() === day;
  }

  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
  }

  function getMonthName(month) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month];
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
    
    // Get days from previous month
    const daysInPrevMonth = currentMonth === 0 
      ? getDaysInMonth(currentYear - 1, 11) 
      : getDaysInMonth(currentYear, currentMonth - 1);
    
    const prevMonthDays = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      const day = daysInPrevMonth - firstDayOfMonth + i + 1;
      const month = currentMonth === 0 ? 11 : currentMonth - 1;
      const year = currentMonth === 0 ? currentYear - 1 : currentYear;
      prevMonthDays.push({ day, month, year, currentMonth: false });
    }
    
    // Current month days
    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({ 
        day: i, 
        month: currentMonth, 
        year: currentYear, 
        currentMonth: true,
        isToday: isToday(currentYear, currentMonth, i),
        isSelected: isSelected(currentYear, currentMonth, i)
      });
    }
    
    // Next month days to fill the calendar grid
    const totalDays = prevMonthDays.length + currentMonthDays.length;
    const nextMonthDays = [];
    const remainingDays = 42 - totalDays; // 6 rows of 7 days
    
    for (let i = 1; i <= remainingDays; i++) {
      const month = currentMonth === 11 ? 0 : currentMonth + 1;
      const year = currentMonth === 11 ? currentYear + 1 : currentYear;
      nextMonthDays.push({ day: i, month, year, currentMonth: false });
    }
    
    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  }

  $: calendarDays = generateCalendarDays();
  
  // When selected date changes, update the current month/year to match
  $: if (selectedDate) {
    currentMonth = selectedDate.getMonth();
    currentYear = selectedDate.getFullYear();
  }
</script>

{#if show}
<div class="modal-backdrop" transition:fade={{ duration: 200 }}>
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-content bg-white dark:bg-gray-800 text-gray-800 dark:text-white">
      <div class="modal-header border-b border-gray-200 dark:border-gray-700">
        <h2 id="modal-title" class="text-xl font-semibold">{title}</h2>
        <button 
          on:click={handleCancel}
          class="close-button text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      
      <div class="modal-body">
        <p class="mb-4 text-sm text-gray-600 dark:text-gray-300">{message}</p>
        
        <div class="selected-date mb-4">
          <!-- svelte-ignore a11y-label-has-associated-control -->
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Selected Date:</label>
          <div class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
            {formatDateForDisplay(selectedDate)}
          </div>
        </div>
        
        <div class="calendar-container">
          <div class="calendar-header flex justify-between items-center mb-2">
            <button on:click={previousMonth} class="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span class="font-medium">{getMonthName(currentMonth)} {currentYear}</span>
            <button on:click={nextMonth} class="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <div class="calendar-grid">
            <div class="grid grid-cols-7 gap-1 text-center mb-1">
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Su</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Mo</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Tu</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">We</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Th</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Fr</span>
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Sa</span>
            </div>
            <div class="grid grid-cols-7 gap-1 text-center">
              {#each calendarDays as { day, month, year, currentMonth, isToday, isSelected }}
                <button 
                  on:click={() => selectDate(year, month, day)}
                  class="
                    w-8 h-8 flex items-center justify-center rounded-full text-sm
                    {currentMonth ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600'}
                    {isToday && !isSelected ? 'border border-blue-500' : ''}
                    {isSelected ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
                  "
                >
                  {day}
                </button>
              {/each}
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer border-t border-gray-200 dark:border-gray-700">
        <button 
          on:click={handleCancel} 
          class="btn-secondary mr-2 px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          {cancelButtonText}
        </button>
        <button 
          on:click={handleConfirm} 
          class="btn-primary px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {confirmButtonText}
        </button>
      </div>
    </div>
  </div>
</div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal {
    width: 100%;
    max-width: 400px;
    margin: 0 1rem;
  }
  
  .modal-content {
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
  }
  
  .close-button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.25rem;
  }
  
  .modal-body {
    padding: 1.5rem 1rem;
  }
  
  .modal-footer {
    padding: 1rem;
    display: flex;
    justify-content: flex-end;
  }
  
  .calendar-container {
    margin: 0 auto;
    width: 100%;
    max-width: 272px;
  }
</style> 